import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  FaqSpecialtiesList,
  addSpecialityFaq,
  listSpecialityFaq,
  editSpecialityFaq,
  updateSpecialityFaq,
  trashSpecialityFaq,
  fetchSpecialtiesList,
} from "../../Adapters/ApiAdapter";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SpecialityFaqList = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editFaq, setEditFaq] = useState(null);
  const [deleteFaqId, setDeleteFaqId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [specialities, setSpecialities] = useState([]);
  const [loadingSpecialities, setLoadingSpecialities] = useState(true);
  const [selectedSpecialityId, setSelectedSpecialityId] = useState("");
  const [newFaqs, setNewFaqs] = useState([
    { question: "", answer: "" },
  ]);
  const [newFaq, setNewFaq] = useState({
    question: "",
    answer: "",
    speciality_id: "",
  });

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await listSpecialityFaq();
        setFaqs(response?.data || []);
      } catch (err) {
        setError("Failed to load FAQ data.");
      } finally {
        setLoading(false);
      }
    };

    const loadSpecialities = async () => {
      try {
        const specialtiesArray = await FaqSpecialtiesList();
        setSpecialities(specialtiesArray);
      } catch (err) {
        toast.error("Failed to load specialties.");
        setSpecialities([]); // Ensure it's an array even on error
      }
    };

    loadSpecialities();
    loadFaqs();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchSpecialtiesList();
        if (response && response.specialities) {
          setSpecialities(response.specialities);
        } else {
          toast.error("Failed to load specialities.");
        }
      } catch (error) {
        toast.error("Error fetching specialities.");
      } finally {
        setLoadingSpecialities(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editSpecialityFaq(id);
      setEditFaq(response?.data);
    } catch (err) {
      toast.error("Failed to load FAQ details.");
    }
  };

  // const handleAddFaq = async () => {
  //   const formData = new FormData();
  //   formData.append("question", newFaq.question);
  //   formData.append("answer", newFaq.answer);
  //   formData.append("speciality_id", newFaq.speciality_id);

  //   setActionLoading(true);
  //   try {
  //     const response = await addSpecialityFaq(formData);
  //     toast.success("FAQ added successfully!");
  //     setFaqs((prev) => [...prev, response.data]);
  //     setNewFaq({ question: "", answer: "", speciality_id: "" });
  //     setAddDialogOpen(false);
  //   } catch (err) {
  //     toast.error("Failed to add FAQ.");
  //   } finally {
  //     setActionLoading(false);
  //   }
  // };

  const handleAddFaq = async () => {
    if (!selectedSpecialityId) {
      toast.error("Please select a Speciality.");
      return;
    }

    setActionLoading(true);

    try {
      const addedFaqs = [];

      for (const faq of newFaqs) {
        if (!faq.question.trim() || !faq.answer.trim()) continue;

        const formData = new FormData();
        formData.append("speciality_id", selectedSpecialityId);
        formData.append("question", faq.question);
        formData.append("answer", faq.answer);

        const response = await addSpecialityFaq(formData);
        if (response?.data) {
          addedFaqs.push(response.data);
        }
      }

      if (addedFaqs.length > 0) {
        toast.success("FAQs added successfully!");
        setFaqs((prev) => [...prev, ...addedFaqs]);
      } else {
        toast.warning("No valid FAQs were added.");
      }

      setNewFaqs([{ question: "", answer: "" }]);
      setSelectedSpecialityId("");
      setAddDialogOpen(false);
    } catch (err) {
      toast.error("Failed to add FAQs.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("question", editFaq.question);
    formData.append("answer", editFaq.answer);
    formData.append("speciality_id", editFaq.speciality_id);

    setActionLoading(true);
    try {
      const response = await updateSpecialityFaq(editFaq.id, formData);
      toast.success("FAQ updated successfully!");
      setFaqs((prev) =>
        prev.map((faq) => (faq.id === editFaq.id ? response.data : faq))
      );
      setEditFaq(null);
    } catch (err) {
      toast.error("Failed to update FAQ.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setActionLoading(true);
    try {
      await trashSpecialityFaq(id);
      toast.success("FAQ deleted successfully!");
      setFaqs((prev) => prev.filter((faq) => faq.id !== id));
      setDeleteFaqId(null);
    } catch (err) {
      toast.error("Failed to delete FAQ.");
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "question", headerName: "Question", flex: 1 },
    { field: "answer", headerName: "Answer", flex: 2 },
    { field: "speciality_name", headerName: "Speciality Name", width: 150 },
    // {
    //   field: "speciality_name",
    //   headerName: "Speciality",
    //   flex: 1,
    //   valueGetter: (params) => {
    //     const speciality = specialities.find((s) => s.id === params.row.speciality_id);
    //     return speciality ? speciality.name : "N/A";
    //   },
    // },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            style={{ marginLeft: 8 }}
            onClick={() => setDeleteFaqId(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={3}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Speciality FAQs
      </Typography>

      <Box mb={2}>
        <Button
          variant="contained"
          color="success"
          onClick={() => setAddDialogOpen(true)}
          style={{background:'#0ca84f'}}
        >
          Add New FAQ
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <DataGrid rows={[...faqs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))}
        columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editFaq && (
        <Dialog open onClose={() => setEditFaq(null)} fullWidth maxWidth="sm">
          <DialogTitle>Edit FAQ</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Question"
              value={editFaq.question}
              onChange={(e) =>
                setEditFaq({ ...editFaq, question: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Answer"
              value={editFaq.answer}
              multiline
              rows={4}
              onChange={(e) =>
                setEditFaq({ ...editFaq, answer: e.target.value })
              }
              margin="normal"
            />

            {loadingSpecialities ? (
              <CircularProgress size={24} />
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel id="speciality-label">Speciality</InputLabel>
                <Select
                  labelId="speciality-label"
                  name="speciality_id"
                  value={editFaq.speciality_id}
                  onChange={(e) =>
                    setEditFaq({ ...editFaq, speciality_id: e.target.value })
                  }
                  required
                >
                  {specialities.map((speciality) => (
                    <MenuItem key={speciality.id} value={speciality.id}>
                      {speciality.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* <TextField
              fullWidth
              label="Speciality ID"
              type="number"
              value={editFaq.speciality_id}
              onChange={(e) =>
                setEditFaq({ ...editFaq, speciality_id: e.target.value })
              }
              margin="normal"
            /> */}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditFaq(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add FAQ Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New FAQ</DialogTitle>
        <DialogContent>
          {/* <TextField
            fullWidth
            label="Question"
            value={newFaq.question}
            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Answer"
            multiline
            rows={4}
            value={newFaq.answer}
            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="speciality-label">Speciality</InputLabel>
            <Select
              labelId="speciality-label"
              value={newFaq.speciality_id}
              onChange={(e) =>
                setNewFaq({ ...newFaq, speciality_id: e.target.value })
              }
              label="Speciality"
            >
              {specialities.map((spec) => (
                <MenuItem key={spec.id} value={spec.id}>
                  {spec.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="speciality-label">Speciality</InputLabel>
            <Select
              labelId="speciality-label"
              value={selectedSpecialityId}
              onChange={(e) => setSelectedSpecialityId(e.target.value)}
              label="Speciality"
              required
            >
              {specialities.map((spec) => (
                <MenuItem key={spec.id} value={spec.id}>
                  {spec.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {newFaqs.map((faq, index) => (
            <Box key={index} mt={2}>
              <TextField
                fullWidth
                label={`Question ${index + 1}`}
                value={faq.question}
                onChange={(e) => {
                  const updatedFaqs = [...newFaqs];
                  updatedFaqs[index].question = e.target.value;
                  setNewFaqs(updatedFaqs);
                }}
                margin="dense"
              />
              <TextField
                fullWidth
                label={`Answer ${index + 1}`}
                value={faq.answer}
                multiline
                rows={3}
                onChange={(e) => {
                  const updatedFaqs = [...newFaqs];
                  updatedFaqs[index].answer = e.target.value;
                  setNewFaqs(updatedFaqs);
                }}
                margin="dense"
              />
            </Box>
          ))}

          <Button
            variant="outlined"
            color="primary"
            onClick={() => setNewFaqs([...newFaqs, { question: "", answer: "" }])}
            style={{ marginTop: "10px", background:'#0ca84f' }}
          >
            + Add Another FAQ
          </Button>

        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddDialogOpen(false)}
            variant="outlined"
            style={{ fontWeight: "bold", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddFaq}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      {deleteFaqId && (
        <Dialog open onClose={() => setDeleteFaqId(null)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this FAQ?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteFaqId(null)}
              variant="outlined"
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(deleteFaqId)}
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={20} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SpecialityFaqList;
